<template>
    <div class="whit-bg">
        <span v-active-touch class="primary-btn payment-btn" @click="handlePayment">
            <img
                v-if="iconPath"
                :src="iconPath"
                :alt="`${selectedMethod} icon`"
            />
            <span>{{ buttonText }}</span>
        </span>
    </div>
</template>

<script>
import stripeHandler from '@/services/paymentHandlers/stripeHandler'
import fortisHandler from '@/services/paymentHandlers/fortisHandler'
import oneclickHandler from '@/services/paymentHandlers/oneclickHandler'
import thawaniHandler from '@/services/paymentHandlers/thawaniHandler'
import moyasarHandler from '@/services/paymentHandlers/moyasarHandler'
import { paymentService } from '@/services/paymentService'

export default {
    name: 'PaymentButton',
    props: {
        selectedMethod: {
            type: String,
            required: true
        },
        billingRule: {
            type: Object,
            default: () => ({})
        },
        deviceCode: {
            type: [String, Number],
            default: ''
        },
        selectedSubscription: {
            type: Object,
            default: () => ({})
        },
        authorizationSubscriptionId: {
            type: [String, Number],
            default: undefined,
        },
        paymentGateway: {
            type: String,
            default: ''
        },
        config: {
            type: Object,
            default: () => ({})
        },
        availabilityResolved: {
            type: Boolean,
            default: false
        },
        availabilityAvailable: {
            type: Boolean,
            default: false
        }
    },
    data () {
        return {
            paymentClient: null,
            isHandlingPayment: false,
        }
    },
    mounted () {
        this.initializePayment();
    },
    watch: {
        billingRule: {
            handler() {
                this.initializePayment();
            },
            deep: true
        },
        selectedSubscription: {
            handler() {
                this.initializePayment();
            },
            deep: true
        },
        'config.stripe': {
            handler(val) {
                if (val) {
                    this.initializePayment();
                }
            }
        },
        'config.fortisGatewayId': {
            handler(val) {
                if (val) {
                    this.initializePayment();
                }
            }
        }
    },
    computed: {
        buttonText () {
            if (
                this.selectedMethod === 'card' ||
                this.selectedMethod === 'creditcard' ||
                this.selectedMethod === 'oneclick' ||
                this.selectedMethod === 'thawani'
            ) {
                return this.$t('m.confirm')
            }
            return this.$t('m.pay')
        },
        iconPath () {
            if (
                this.selectedMethod === 'web_apple_pay' ||
                this.selectedMethod === 'apple_pay' ||
                this.selectedMethod === 'applepay'
            ) {
                return require('@/assets/payments/Apple_white.svg')
            }
            if (this.selectedMethod === 'web_google_pay' || this.selectedMethod === 'google_pay') {
                return require('@/assets/payments/Google_white.svg')
            }
            return null
        }
    },
    methods: {
        shouldKeepPendingRow() {
            return [
                'apple_pay',
                'google_pay',
                'web_apple_pay',
                'web_google_pay',
                'applepay'
            ].includes(this.selectedMethod)
        },
        shouldCheckAvailability() {
            return this.shouldKeepPendingRow()
        },
        emitAvailabilityState(isAvailable, extra = {}) {
            this.$emit('update-available', this.selectedMethod, isAvailable, extra)
        },
        async initializePayment() {
            console.log('初始化支付配置：', this.selectedMethod, this.billingRule, this.$store.getters['repayment_info'], this.config.stripe, this.deviceCode, this.selectedSubscription);

            const handlers = {
                stripe: stripeHandler,
                fortis: fortisHandler,
                oneclick: oneclickHandler,
                thawani: thawaniHandler,
                moyasar: moyasarHandler
            };
            const handler = handlers[this.paymentGateway];

            if (!handler) {
                console.warn(`未找到支付网关处理程序: ${this.paymentGateway}`);
                return;
            }

            // 如果是 Stripe 网关但 config.stripe 为空，则等待 watch config.stripe 触发
            if (this.paymentGateway === 'stripe' && !this.config.stripe) {
                console.log('Stripe 实例尚未就绪，等待初始化...');
                return;
            }

            try {
                if (this.shouldCheckAvailability() && this.availabilityResolved && !this.availabilityAvailable) {
                    return;
                }

                if (this.shouldKeepPendingRow()) {
                    this.emitAvailabilityState(false, {
                        checking: true,
                        resolved: false,
                    })
                }

                this.paymentClient = await handler.initPayment({
                    selectedMethod: this.selectedMethod,
                    billingRule: this.billingRule,
                    repaymentInfo: this.$store.getters['repayment_info'],
                    stripe: this.config.stripe,
                    selectedSubscription: this.selectedSubscription
                });

                if (!this.shouldCheckAvailability()) {
                    return;
                }

                const result = await handler.checkAvailability({
                    selectedMethod: this.selectedMethod,
                    paymentClient: this.paymentClient
                });

                // 兼容处理：支持返回对象 { available, reason } 或直接返回 boolean
                const isAvailable = result && typeof result === 'object' && 'available' in result
                    ? result.available
                    : !!result;
                const reason = result && result.reason ? result.reason : '';

                this.emitAvailabilityState(isAvailable, {
                    checking: false,
                    resolved: true,
                    reason,
                })
                console.log(this.paymentGateway, this.selectedMethod, isAvailable, reason);

            } catch (err) {
                console.error(`初始化 ${this.paymentGateway} 失败:`, err);
                if (!this.shouldCheckAvailability()) {
                    return;
                }
                this.emitAvailabilityState(false, {
                    checking: false,
                    resolved: true,
                    reason: err && err.message,
                });
            }
        },
        async handlePayment () {
            if (this.isHandlingPayment) {
                return
            }
            // 如果是 card 支付，调用父组件的 submitCardPayment 方法
            if (this.selectedMethod === 'card') {
                if (this.$parent && typeof this.$parent.submitCardPayment === 'function') {
                    this.$parent.submitCardPayment();
                } else {
                    console.error('Parent component does not have submitCardPayment method');
                    this.$emit('toast', 'Card payment method not implemented in parent');
                }
                return;
            }

            const handlers = {
                stripe: stripeHandler,
                fortis: fortisHandler,
                oneclick: oneclickHandler,
                thawani: thawaniHandler,
                moyasar: moyasarHandler
            }
            const handler = handlers[this.paymentGateway]
            const usesInternalLoading =
                handler === oneclickHandler ||
                handler === thawaniHandler ||
                handler === moyasarHandler

            try {
                this.isHandlingPayment = true
                if (usesInternalLoading) {
                    this.showLoading()
                } else {
                    this.$emit('show-loading')
                }
                await handler.handlePayment({
                    selectedMethod: this.selectedMethod,
                    paymentClient: this.paymentClient,
                    billingRule: this.billingRule,
                    selectedSubscription: this.selectedSubscription,
                    repaymentInfo: this.$store.getters['repayment_info'],
                    deviceCode: this.$store.getters['appConfig/device_code'],
                    outletId: this.$store.getters['appConfig/outlet_id'],
                    config: this.config,
                    authorizationSubscriptionId:
                        this.authorizationSubscriptionId,
                    createPaymentOrder: this.createPaymentOrder,
                    completePaymentOrder: this.completePaymentOrder,
                    doSubscriptionPayment: this.doSubscriptionPayment,
                    doRepay: this.doRepay,
                    doBindCardPayment: this.doBindCardPayment,
                    afterPayment: data => this.$emit('after-payment', paymentService.makeAfterPaymentPayload({
                        provider: this.paymentGateway,
                        method: this.selectedMethod,
                        ...data
                    })),
                    toast: msg => this.$emit('toast', msg),
                    hideLoading: () => {
                        if (usesInternalLoading) {
                            this.hideLoading()
                        } else {
                            this.$emit('hide-loading')
                        }
                    }
                })
            } catch (err) {
                this.$emit('toast', `支付失败: ${err.message}`)
                if (usesInternalLoading) {
                    this.hideLoading()
                } else {
                    this.$emit('hide-loading')
                }
            } finally {
                this.isHandlingPayment = false
            }
        },
        // 代理父组件方法，保持兼容性
        createPaymentOrder (...args) {
            return this.$parent.createPaymentOrder(...args)
        },
        completePaymentOrder (...args) {
            return this.$parent.completePaymentOrder(...args)
        },
        doSubscriptionPayment (...args) {
            return this.$parent.doSubscriptionPayment(...args)
        },
        doRepay (...args) {
            return this.$parent.doRepay(...args)
        },
        doBindCardPayment (...args) {
            return this.$parent.doBindCardPayment(...args)
        }
    }
}
</script>

<style lang="scss" scoped>
@use '@/styles/variables.scss' as *;

.whit-bg {
    background-color: $background-white;
    width: 100%;
    height: 90px;
    position: fixed;
    bottom: 0;
    box-shadow: 0px -4px 9.6px 0px rgba(0, 0, 0, 0.08);
}

.primary-btn.payment-btn {
    width: $container-width;
    position: fixed;
    bottom: 30px;
    left: 50%;
    transform: translateX(-50%);
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 9px;
    background: var(--theme-primary-btn-bg);
    color: $background-white;
    font-size: 16px;
    line-height: 1;
    padding: 12px;
    border-radius: $border-radius;

    img {
        width: 20px;
    }
}
</style>
